import * as React from "react";
import { SvgIcon, SvgIconProps, Button, ButtonProps } from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";

// util
import { classNames } from "@util/html.util";

// styles
import { greys, colors } from "@styles/color.styles";
import { animated, useSpring, config } from "@react-spring/web";
import { CWIDProps } from "@util/cw-id.util";

export interface CardProps {
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  onClick?: React.HTMLAttributes<HTMLElement>["onClick"];
  variant?: "outlined" | "raised" | "borderless";
  leftContent?: React.HTMLAttributes<HTMLDivElement>["children"];
  rightContent?: React.HTMLAttributes<HTMLDivElement>["children"];
  children?: React.HTMLAttributes<HTMLDivElement>["children"];
  leftContentClassName?: string;
  rightContentClassName?: string;
  childrenClassName?: string;
}

export interface CenteredCardProps {
  variant?: "outlined" | "dashed";
  content?: React.HTMLAttributes<HTMLDivElement>["children"] | boolean;
  subContent?: React.HTMLAttributes<HTMLDivElement>["children"] | boolean;
  onClick?: React.HTMLAttributes<HTMLElement>["onClick"];
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  color?: "blue" | "green" | "yellow";
  scaleOnHover?: boolean;
  onSpaceOrEnter?: (e: React.KeyboardEvent<HTMLElement>) => void;
  tabIndex?: number;
  "aria-label"?: string;
}

export interface CardIconProps extends Omit<CardProps, "leftContent"> {
  Icon: typeof SvgIcon;
  iconProps?: SvgIconProps;
}

export interface CardButtonProps extends Omit<CardProps, "rightContent"> {
  buttonProps?: ButtonProps;
}

const useCardStyles = makeStyles({
  container: {
    display: "flex",
    alignItems: "center",
    position: "relative",
    borderRadius: 8,
    marginTop: 16,
    padding: "12px 14px",
    background: greys.white,
    backgroundColor: greys.white,
    overflow: "hidden",
    userSelect: "none",

    "&.centered": {
      justifyContent: "center",
      flexDirection: "column",
      marginTop: 8,
    },

    "&.outlined": {
      boxShadow: "none",
      borderStyle: "solid",
      borderWidth: 1,
      borderColor: greys.cloudyBlue,
    },

    "&.dashed": {
      borderStyle: "dashed",
      borderWidth: 1,
      borderColor: greys.cloudyBlue,
      boxShadow: "none",
    },

    "&.raised": {
      boxShadow: `rgba(0, 0, 0, .12) 0 0 2px 0, rgba(0, 0, 0, .24) 0 2px 2px 0`,

      "&.actionable:active": {
        boxShadow: `rgba(0, 0, 0, .12) 0 0 1px 0, rgba(0, 0, 0, .24) 0 1px 1px 0`,
      },
    },

    "&.blue p": {
      color: colors.blueDusk.darker,
    },

    "&.green p": {
      color: colors.green.darker,
    },

    "&.yellow p": {
      color: colors.yellow.darker,
    },

    "&.actionable": {
      cursor: "pointer",
      "&:hover": {
        background: greys.palePaleGrey,
        backgroundColor: greys.palePaleGrey,
      },
      "&:active": {
        background: greys.paleGrey,
        backgroundColor: greys.paleGrey,
      },
    },

    "& > .children": {
      flex: 1,
    },

    "& > .left-content": {
      paddingRight: 14,
      marginBottom: -3,
    },

    "& > .right-content": {
      paddingLeft: 14,
    },
  },
});

const useCardIconStyles = makeStyles({
  icon: {
    fontSize: 20,
  },
});

export const Card = ({
  leftContent,
  rightContent,
  onClick,
  className,
  children,
  variant = "outlined",
  leftContentClassName,
  rightContentClassName,
  childrenClassName,
}: CardProps) => {
  const classes = useCardStyles();

  return (
    <div
      cw-id="card"
      onClick={onClick}
      className={classNames([
        classes.container,
        onClick ? "actionable" : undefined,
        variant,
        className,
      ])}
    >
      {leftContent && (
        <div className={classNames(["left-content", leftContentClassName])}>{leftContent}</div>
      )}
      <div className={classNames(["children", childrenClassName])}>{children}</div>
      {rightContent && (
        <div className={classNames(["right-content", rightContentClassName])}>{rightContent}</div>
      )}
    </div>
  );
};

export const CenteredCard = ({
  content,
  subContent,
  onClick,
  onSpaceOrEnter,
  className,
  variant = "outlined",
  color = "blue",
  scaleOnHover = true,
  tabIndex,
  "cw-id": cwId,
  "aria-label": ariaLabel,
}: CenteredCardProps & CWIDProps) => {
  const classes = useCardStyles();

  const [hovering, setHovering] = React.useState(false);

  const [{ scale }] = useSpring(
    {
      scale: hovering ? 1.065 : 1.0,
      config: { ...config.stiff, delay: 0 },
    },
    [hovering],
  );

  return (
    <animated.div
      style={{
        scale,
      }}
      aria-label={ariaLabel}
      cw-id={cwId ?? "centered-card"}
      tabIndex={tabIndex}
      onClick={onClick}
      onKeyDown={onSpaceOrEnter}
      onMouseEnter={scaleOnHover ? () => setHovering(true) : undefined}
      onMouseLeave={scaleOnHover ? () => setHovering(false) : undefined}
      className={classNames([
        className,
        classes.container,
        "centered",
        onClick ? "actionable" : undefined,
        variant,
        color,
      ])}
    >
      {content}
      {subContent}
    </animated.div>
  );
};

export const CardIcon = ({ Icon, iconProps, ...cardProps }: CardIconProps) => {
  const classes = useCardIconStyles();

  return (
    <Card
      leftContent={
        <Icon {...iconProps} className={classNames([classes.icon, iconProps?.className])} />
      }
      {...cardProps}
    />
  );
};

export const CardButton = ({ buttonProps, ...cardProps }: CardButtonProps) => {
  return (
    <Card
      rightContent={
        <Button
          className={buttonProps?.className}
          variant="outlined"
          onClick={buttonProps?.onClick}
        >
          {buttonProps?.children}
        </Button>
      }
      {...cardProps}
    />
  );
};
